import React from 'react';
import { AppLayout } from './AppLayout';
import { PageHeader } from './PageHeader';
import { DashboardDataProvider } from '@/contexts/DashboardDataContext';
import { EtablissementProvider } from '@/contexts/EtablissementContext';
import { LayoutProvider, LayoutType, useOptionalLayout } from '@/contexts/LayoutContext';
import { SidebarProvider, useOptionalSidebar } from '@/components/ui/sidebar';
import { useEffect } from 'react';

interface AppLayoutWithHeaderProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  showSidebar?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  layoutMode?: LayoutType;
  sidebarVisible?: boolean;
}

/**
 * ✅ PHASE 1B : AppLayoutWithHeader avec DashboardDataProvider
 * 
 * Le Provider est monté au niveau du layout authentifié pour que :
 * - PageHeader ait accès aux notifications
 * - Toutes les pages enfants puissent consommer useDashboardData()
 * - 1 seul appel à useDashboardWithPolling (centralisé)
 * 
 * Gating isReady : Le Provider attend userId + orgId + userRole avant de rendre
 */
export const AppLayoutWithHeader: React.FC<AppLayoutWithHeaderProps> = ({ 
  children, 
  title,
  subtitle: _subtitle,
  showSidebar = true,
  showBackButton = false,
  onBack,
  layoutMode = 'default',
  sidebarVisible = true,
}) => {
  const layout = useOptionalLayout();
  const sidebar = useOptionalSidebar();

  useEffect(() => {
    if (!layout) return;

    if (layout.currentLayout !== layoutMode) {
      layout.setCurrentLayout(layoutMode);
    }

    if (layout.sidebarVisible !== sidebarVisible) {
      layout.setSidebarVisible(sidebarVisible);
    }
  }, [layout, layoutMode, sidebarVisible]);

  let content = (
    <DashboardDataProvider>
      <EtablissementProvider>
        <AppLayout showSidebar={showSidebar}>
          <div className="flex-1 flex flex-col px-0 transition-all duration-300 font-sans">
            <main className="flex-1 px-4 py-2.5 md:px-5 md:py-3 xl:px-6">
              <div className="w-full flex flex-col gap-3.5 md:gap-4">
                <PageHeader 
                  title={title} 
                  showBackButton={showBackButton}
                  onBack={onBack}
                />
                {children}
              </div>
            </main>
          </div>
        </AppLayout>
      </EtablissementProvider>
    </DashboardDataProvider>
  );

  if (!layout) {
    content = (
      <LayoutProvider
        defaultLayout={layoutMode}
        defaultSidebarVisible={sidebarVisible}
      >
        {content}
      </LayoutProvider>
    );
  }

  if (!sidebar) {
    content = <SidebarProvider defaultOpen>{content}</SidebarProvider>;
  }

  return content;
};
